// Type-only on purpose — see the note in build-selected-solution.ts.
import type { SolutionVersion } from '../xstate/prostudio.api-service.types';
import {
  readDeploymentPhase,
  readDeploymentStep,
  type DeploymentPhase,
} from './deployment-progress';

export type DeploymentStatusView = {
  phase: DeploymentPhase;
  /** The `ps-tag` variant that renders it. */
  variant: 'success' | 'error' | 'default';
  /** The step a running publish is on, or the one a failed publish stopped at. */
  step?: string;
};

const VARIANT_BY_PHASE: Record<
  DeploymentPhase,
  DeploymentStatusView['variant']
> = {
  succeeded: 'success',
  failed: 'error',
  in_progress: 'default',
};

/**
 * How a Versions table row reports its publish attempt (PSTUDIO-4354). `undefined` for a
 * version that was never deployed: it has no attempt to report.
 */
export const describeDeploymentStatus = (
  status?: string | null
): DeploymentStatusView | undefined => {
  if (!status?.trim()) return undefined;
  const phase = readDeploymentPhase(status);
  return {
    phase,
    variant: VARIANT_BY_PHASE[phase],
    step: readDeploymentStep(status),
  };
};

/**
 * Folds the Versions table's entries into display rows: one version published by the same
 * person to several environments reads as one row, `live, sandbox`, dated by the newest.
 *
 * The status is part of the key, so attempts that ended differently — finished to sandbox,
 * failed to live — stay two rows, each with its own outcome.
 */
export const mergeVersionRows = (
  data: SolutionVersion[]
): SolutionVersion[] => {
  const versionMap = new Map<string, SolutionVersion & { env: string }>();

  data.forEach((item) => {
    const key = JSON.stringify({
      picture: item.picture,
      bot: item.bot,
      customer: item.customer,
      email: item.email,
      first_name: item.first_name,
      last_name: item.last_name,
      uuid: item.uuid,
      version: item.version,
      status: item.status ?? '',
    });

    const existing = versionMap.get(key);

    if (existing) {
      const envSet = new Set(
        existing.env.split(', ').concat(item.env.toLowerCase())
      );
      existing.env = Array.from(envSet).sort().join(', ');
      if (new Date(item.time) > new Date(existing.time)) {
        existing.time = item.time;
      }
    } else {
      versionMap.set(key, { ...item, env: item.env.toLowerCase() });
    }
  });

  return Array.from(versionMap.values());
};

export type RowEnvironmentState = { env: string; isActive: boolean };

/**
 * Splits a Versions table row's `env` (`live, sandbox`, as `mergeVersionRows` writes it) into
 * one entry per environment, each saying whether the row's version is what that environment
 * runs now. The environments of one row are judged separately: a version published to both
 * can still sit in Live while Sandbox has moved on.
 *
 * `deployed` holds `[env, version]` tuples, the shape the Versions page builds for its
 * environment cards; names are compared lower-cased and versions strictly, so a `null` row
 * version matches nothing, an empty-version tuple included.
 *
 * Limitation: a row is judged by its version and its environments only, never by its own
 * `status`. `deployed` comes from `deriveDeployedVersions`, which counts `finished`
 * deployments only (`FINISHED` in derive-deployments.ts), so an environment whose latest
 * attempt failed or is still running keeps pointing at its last finished version: a row of
 * that version is highlighted there whatever status the row carries, and a row of the version
 * being attempted is not highlighted until the attempt finishes.
 */
export const describeRowEnvironments = (
  rowEnv: string,
  rowVersion: string | null,
  deployed: ReadonlyArray<readonly [string, string]>
): RowEnvironmentState[] =>
  rowEnv
    .split(', ')
    .map((env) => env.trim().toLowerCase())
    .filter(Boolean)
    .map((env) => ({
      env,
      isActive: deployed.some(
        ([deployedEnv, deployedVersion]) =>
          deployedEnv.toLowerCase() === env && deployedVersion === rowVersion
      ),
    }));

/**
 * The row an environment card summarises: the version deployed there, PUBLISHED. The
 * history now keeps running and failed attempts too, and the backend keeps one row per
 * version and environment — so while that version is being republished, its row describes
 * the attempt, not the publish the card reports. A row without a status (never deployed, or
 * from before statuses were kept) is taken as it is.
 */
export const pickPublishedRow = (
  rows: SolutionVersion[],
  version: string,
  env: string
): SolutionVersion | undefined =>
  rows.find(
    (row) =>
      row.version === version &&
      row.env === env &&
      (!row.status || readDeploymentPhase(row.status) === 'succeeded')
  );
